// redux/slices/masterSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const handleAuthError = async (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch { }
  }
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const normalizeMaster = (payload) => payload?.master || payload || {};

/* ===================== PUBLIC THUNKS ===================== */

export const fetchMasterByLinkKey = createAsyncThunk(
  "master/fetchMasterByLinkKey",
  async (masterLinkKey, { rejectWithValue }) => {
    try {
      if (!masterLinkKey) throw new Error("Master link key not provided");

      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(
        `${base}/public/master/${encodeURIComponent(masterLinkKey)}`
      );

      return data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || {
          error: error?.message || "Network error",
        }
      );
    }
  }
);

export const fetchPublicMaster = createAsyncThunk(
  "master/fetchPublicMaster",
  async (masterId, { rejectWithValue }) => {
    try {
      if (!masterId) throw new Error("Master ID not found");

      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(`${base}/public/check/${masterId}`);

      return data?.master || {};
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch public master")
      );
    }
  }
);

export const fetchPublicEmployees = createAsyncThunk(
  "employees/fetchPublicEmployees",
  async (parentUid, { rejectWithValue }) => {
    try {
      if (!parentUid) throw new Error("parentUid не передан");

      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(`${base}/public/${parentUid}/employees`);

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        "Failed to fetch public employees"
      );
    }
  }
);

/* ===================== PRIVATE THUNKS ===================== */

export const fetchMaster = createAsyncThunk(
  "master/fetchMaster",
  async (masterId, { rejectWithValue }) => {
    try {
      if (!masterId) throw new Error("Master ID not found");

      const res = await apiRequest("GET", `/masters/check/${masterId}`);
      return res?.data?.master || res?.master || {};
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getErrorMessage(error, "Failed to fetch master"));
    }
  }
);

export const fetchAllMasters = createAsyncThunk(
  "master/fetchAllMasters",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", "/masters");
      const data = res?.data || res;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getErrorMessage(error, "Failed to fetch masters"));
    }
  }
);

export const fetchMasterByPhone = createAsyncThunk(
  "master/fetchMasterByPhone",
  async (phoneNumber) => {
    try {
      if (!phoneNumber) throw new Error("Phone number is required");

      const res = await apiRequest(
        "GET",
        `/masters/by-phone/${encodeURIComponent(phoneNumber)}`
      );

      return res?.data?.masterLink || res?.masterLink || null;
    } catch {
      return null;
    }
  }
);

export const createMaster = createAsyncThunk(
  "master/createMaster",
  async (masterData, { rejectWithValue }) => {
    try {
      const res = await apiRequest("POST", "/masters", masterData);
      return normalizeMaster(res?.data || res);
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getErrorMessage(error, "Failed to create master"));
    }
  }
);

export const updateMaster = createAsyncThunk(
  "master/updateMaster",
  async (masterData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const uid = masterData?.uid || state.master?.master?.uid || state.master?.uid;

      if (!uid) throw new Error("UID not found");

      const res = await apiRequest("PUT", `/masters/${uid}`, {
        ...masterData,
        uid,
      });

      return normalizeMaster(res?.data || res);
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getErrorMessage(error, "Failed to update master"));
    }
  }
);

export const fetchEmployees = createAsyncThunk(
  "master/fetchEmployees",
  async (masterUid, { rejectWithValue }) => {
    try {
      if (!masterUid) throw new Error("Master UID is required");

      const res = await apiRequest("GET", `/masters/employees/${masterUid}`);
      const raw = res?.data || res;
      const list = Array.isArray(raw) ? raw : raw?.employees || [];

      return list.map((employee) => ({
        ...employee,
        uid: String(employee.uid),
      }));
    } catch (error) {
      const code = error?.response?.status;
      const msg = getErrorMessage(error, "");

      if (code === 404 || code === 403 || /No employees found/i.test(msg)) {
        return [];
      }

      await handleAuthError(error);
      return rejectWithValue(msg || "Failed to fetch employees");
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  "master/deleteEmployee",
  async (targetUid, { rejectWithValue }) => {
    try {
      if (!targetUid) throw new Error("Target UID is required");

      const res = await apiRequest("DELETE", `/delete-user/${targetUid}`);

      return {
        uid: targetUid,
        message: res?.data?.message || res?.message,
      };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getErrorMessage(error, "Failed to delete user"));
    }
  }
);

export const registerMaster = createAsyncThunk(
  "master/registerMaster",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID is required");

      const res = await apiRequest("POST", `/masters/register/${uid}`);
      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        getErrorMessage(error, "Failed to register master")
      );
    }
  }
);

export const updateLastVisit = createAsyncThunk(
  "master/updateLastVisit",
  async (payload, { rejectWithValue }) => {
    try {
      // Старые вызовы updateLastVisit(uid) продолжат работать.
      const uid = typeof payload === "object" ? payload?.uid : payload;
      if (!uid) throw new Error("UID is required");

      const deviceInfo = typeof payload === "object"
        ? {
          deviceModel: payload.deviceModel,
          timezone: payload.timezone,
        }
        : {};

      await apiRequest("POST", `/masters/visit/${uid}`, deviceInfo);
      return true;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        getErrorMessage(error, "Failed to update last visit")
      );
    }
  }
);

export const updateMasterTimezone = createAsyncThunk(
  "master/updateMasterTimezone",
  async ({ uid, timezone }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const masterUid = uid || state.master?.master?.uid || state.master?.uid;

      if (!masterUid) throw new Error("UID not found");
      if (!timezone) throw new Error("Timezone not provided");

      const res = await apiRequest("POST", `/masters/timezone/${masterUid}`, {
        timezone,
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        getErrorMessage(error, "Failed to update master timezone")
      );
    }
  }
);

export const toggleMasterBlock = createAsyncThunk(
  "master/toggleMasterBlock",
  async ({ uid, blocked }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not found");

      const res = await apiRequest("POST", "/masters/toggle-block", {
        uid,
        blocked,
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(
        getErrorMessage(error, "Failed to toggle master block")
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  masters: [],
  master: null,
  employees: [],
  masterLinkForClient: null,

  uid: "",
  parentUid: null,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  facebookLink: "",
  instagramLink: "",
  dateOfBirth: "",
  isUserRegistered: false,
  isSubscriptionEnabled: false,
  lastVisit: null,
  employeeSeatsLimit: 3,
  timezone: "",
  hearAboutUs: "",
  hearAboutUsCustom: "",
  isBlocked: false,

  status: "idle",
  error: null,
};

/* ===================== SLICE ===================== */

const masterSlice = createSlice({
  name: "master",
  initialState,
  reducers: {
    switchUser: (state, action) => {
      const { uid, parentUid } = action.payload || {};

      state.uid = uid || "";
      state.parentUid = parentUid || null;

      if (state.master) {
        state.master.uid = uid || state.master.uid;
        state.master.parentUid = parentUid || null;
      }
    },

    updateMasterField: (state, action) => {
      const { field, value } = action.payload || {};
      if (!field) return;

      state[field] = value;

      if (state.master) {
        state.master[field] = value;
      }
    },

    setUserRegistered: (state, action) => {
      state.isUserRegistered = !!action.payload;

      if (state.master) {
        state.master.isUserRegistered = !!action.payload;
      }
    },

    setMasterDirectly: (state, action) => {
      const master = action.payload || null;

      state.master = master;
      state.status = "succeeded";

      if (master) {
        Object.assign(state, master);
        state.master = master;
      }
    },

    clearMasterError: (state) => {
      state.error = null;
    },

    resetMasterStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMaster.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMaster.fulfilled, (state, action) => {
        state.status = "succeeded";
        const master = action.payload || {};
        state.master = master;
        Object.assign(state, master);
        state.isUserRegistered = master.isUserRegistered ?? true;
      })
      .addCase(fetchMaster.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        if (!state.master) state.master = null;
      })

      .addCase(fetchPublicMaster.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicMaster.fulfilled, (state, action) => {
        state.status = "succeeded";
        const master = action.payload || {};
        state.master = master;
        Object.assign(state, master);
      })
      .addCase(fetchPublicMaster.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        if (!state.master) state.master = null;
      })

      .addCase(fetchMasterByLinkKey.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterByLinkKey.fulfilled, (state, action) => {
        state.status = "succeeded";
        const master = action.payload || {};
        state.master = master;
        Object.assign(state, master);
      })
      .addCase(fetchMasterByLinkKey.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(fetchAllMasters.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllMasters.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.masters = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllMasters.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(createMaster.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createMaster.fulfilled, (state, action) => {
        state.status = "succeeded";
        const master = action.payload || {};
        state.master = master;
        Object.assign(state, master);
      })
      .addCase(createMaster.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateMaster.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateMaster.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updated = action.payload || {};

        state.master = {
          ...(state.master || {}),
          ...updated,
        };

        Object.assign(state, updated);
      })
      .addCase(updateMaster.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(fetchEmployees.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.employees = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(deleteEmployee.fulfilled, (state, action) => {
        const removedUid = action.payload?.uid || action.meta?.arg;

        state.employees = state.employees.filter(
          (employee) => String(employee.uid) !== String(removedUid)
        );
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(registerMaster.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerMaster.fulfilled, (state) => {
        state.status = "succeeded";
        state.isUserRegistered = true;

        if (state.master) {
          state.master.isUserRegistered = true;
        }
      })
      .addCase(registerMaster.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateLastVisit.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateLastVisit.fulfilled, (state) => {
        state.status = "succeeded";
        const now = new Date().toISOString();
        state.lastVisit = now;

        if (state.master) {
          state.master.lastVisit = now;
        }
      })
      .addCase(updateLastVisit.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(updateMasterTimezone.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateMasterTimezone.fulfilled, (state, action) => {
        state.status = "succeeded";

        const timezone =
          action.payload?.data?.timezone ||
          action.payload?.timezone ||
          action.meta.arg?.timezone;

        state.timezone = timezone || "";

        if (state.master) {
          state.master.timezone = timezone || "";
        }
      })
      .addCase(updateMasterTimezone.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(toggleMasterBlock.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(toggleMasterBlock.fulfilled, (state, action) => {
        state.status = "succeeded";

        const isBlocked =
          action.payload?.data?.isBlocked ??
          action.payload?.isBlocked ??
          action.meta.arg?.blocked ??
          state.isBlocked;

        state.isBlocked = !!isBlocked;

        if (state.master) {
          state.master.isBlocked = !!isBlocked;
        }
      })
      .addCase(toggleMasterBlock.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(fetchPublicEmployees.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicEmployees.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.employees = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPublicEmployees.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch public employees";
        state.employees = [];
      })

      .addCase(fetchMasterByPhone.fulfilled, (state, action) => {
        state.masterLinkForClient = action.payload || null;
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  switchUser,
  updateMasterField,
  setUserRegistered,
  setMasterDirectly,
  clearMasterError,
  resetMasterStatus,
} = masterSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectMasterState = (state) => state.master;
export const selectMaster = (state) => state.master?.master || null;
export const selectMasterUid = (state) =>
  state.master?.master?.uid || state.master?.uid || null;
export const selectEmployees = (state) => state.master?.employees || [];
export const selectMasters = (state) => state.master?.masters || [];
export const selectMasterStatus = (state) => state.master?.status || "idle";
export const selectMasterError = (state) => state.master?.error || null;
export const selectMasterLinkForClient = (state) =>
  state.master?.masterLinkForClient || null;
export const selectIsUserRegistered = (state) =>
  !!(state.master?.master?.isUserRegistered ?? state.master?.isUserRegistered);
export const selectIsSubscriptionEnabled = (state) =>
  !!(
    state.master?.master?.isSubscriptionEnabled ??
    state.master?.isSubscriptionEnabled
  );
export const selectMasterTimezone = (state) =>
  state.master?.master?.timezone || state.master?.timezone || "";

export default masterSlice.reducer;